<template>
  <div class="page-container">
    <!-- Full page background -->
    <div class="full-page-background">
      <img
        src="/img/homepage_bg.webp"
        alt="Background"
        class="background-image"
      />
    </div>

    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">PrEP METHOD QUIZ</h1>
      <p class="page-subtitle">
        To find out which PrEP method may work for you, select all the options
        that are true for you.
      </p>
    </div>

    <!-- Content section -->
    <div class="content-section">
      <div class="content-inner">
        <!-- Current question -->
        <div v-if="currentQuestion" class="question-area">
          <span v-if="currentQuestion.emoji" class="question-emoji">{{
            currentQuestion.emoji
          }}</span>
          <h2 class="question-text">{{ currentQuestion.text }}</h2>

          <div class="quiz-options">
            <div
              v-for="(option, index) in currentOptions"
              :key="index"
              class="choice-item"
            >
              <v-btn
                :class="[
                  'choice-btn',
                  { active: selectedOptionIndex === index },
                ]"
                :aria-pressed="selectedOptionIndex === index"
                @click="selectOption(index)"
                variant="flat"
                size="large"
                block
              >
                <div class="d-flex flex-row align-center w-100">
                  <span
                    class="choice-marker"
                    :class="{ active: selectedOptionIndex === index }"
                    aria-hidden="true"
                  >
                    <span class="choice-marker-dot"></span>
                  </span>
                  <span class="choice-text">{{ option.label }}</span>
                </div>
              </v-btn>
            </div>
          </div>

          <!-- Question progress bar -->
          <div class="quiz-progress-bar">
            <div class="quiz-progress-row">
              <span
                >Question {{ stepIndex + 1 }} of
                {{ sortedQuestions.length }}</span
              >
              <span>{{ Math.round(progress) }}% complete</span>
            </div>
            <div class="quiz-progress-track">
              <div
                class="quiz-progress-fill"
                :style="{ width: progress + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div v-else class="loading-container">
          <v-progress-circular
            indeterminate
            color="#eb63eb"
            size="40"
          ></v-progress-circular>
        </div>
      </div>
    </div>
    <!-- Fixed bottom buttons -->
    <div class="fixed-bottom-buttons">
      <v-app-bar location="bottom" class="bottom-bar" height="96">
        <div class="quiz-bottom-inner">
          <div class="d-flex flex-row align-center justify-space-around w-100">
            <v-btn
              class="back-button"
              size="x-large"
              @click="handleBack"
              variant="elevated"
            >
              BACK
            </v-btn>
            <v-btn
              class="submit-button"
              size="x-large"
              @click="handleNext"
              :disabled="selectedOptionIndex === null"
              variant="elevated"
            >
              {{ isLastQuestion ? "SEE RESULTS" : "NEXT" }}
            </v-btn>
          </div>
        </div>
      </v-app-bar>
    </div>
  </div>
</template>

<script>
import QuestionDataService from "../services/QuestionDataService";
import MethodDataService from "../services/MethodDataService";
// import HeaderBar from "../components/HeaderBar.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  name: "QuizPageOne",
  // components: { HeaderBar },
  data: () => ({
    allQuestions: [],
    allMethods: [],
    stepIndex: 0,
    answers: [],
    selectedOptionIndex: null,
  }),
  async created() {
    this.allQuestions = await QuestionDataService.getAll();
    this.allMethods = await MethodDataService.getAll();
  },
  computed: {
    ...mapGetters(["country"]),
    sortedQuestions() {
      // Sort questions by position (create new array to avoid mutation)
      return [...this.allQuestions].sort((a, b) => a.position - b.position);
    },
    currentQuestion() {
      return this.sortedQuestions[this.stepIndex] || null;
    },
    currentOptions() {
      if (!this.currentQuestion) return [];
      // Questions with explicit options present them as multiple choices
      if (
        this.currentQuestion.options &&
        this.currentQuestion.options.length > 0
      ) {
        return this.currentQuestion.options.map((option) => ({
          label: option.label,
          methods: option.applicableMethods || [],
        }));
      }
      // Otherwise fall back to a simple Yes/No question
      return [
        {
          label: "Yes, this is true for me",
          methods: this.currentQuestion.applicableMethods || [],
        },
        { label: "No, it isn't", methods: [] },
      ];
    },
    progress() {
      if (this.sortedQuestions.length === 0) return 0;
      return (this.stepIndex / this.sortedQuestions.length) * 100;
    },
    isLastQuestion() {
      return this.stepIndex === this.sortedQuestions.length - 1;
    },
    methodScores() {
      // Negation only applies to plain Yes/No questions where the user
      // answered "Yes" — multi-option questions don't have a single
      // "selected" state to negate against.
      const selectedQuestionIds = this.answers
        .filter((answer) => {
          const question = this.sortedQuestions.find((q) => q.id === answer.id);
          return (
            question &&
            (!question.options || question.options.length === 0) &&
            answer.methods.length > 0
          );
        })
        .map((answer) => answer.id);

      // Filter out methods that aren't available in the user's country, or
      // that should be negated based on answered questions — this keeps a
      // method that's been discontinued in a country from ever winning for
      // users there, no matter which answers point to it.
      const availableMethods = this.allMethods.filter((method) => {
        if (
          method.applicableCountries &&
          method.applicableCountries.length > 0 &&
          !method.applicableCountries.includes(this.country)
        ) {
          return false;
        }

        if (
          !method.negatingQuestions ||
          method.negatingQuestions.length === 0
        ) {
          return true;
        }
        return !method.negatingQuestions.some((negatingQuestionId) =>
          selectedQuestionIds.includes(negatingQuestionId),
        );
      });

      // Calculate scores for non-negated methods
      const scores = {};
      this.answers.forEach((answer) => {
        answer.methods.forEach((methodId) => {
          const methodExists = availableMethods.some(
            (method) => method.id === methodId,
          );
          if (methodExists) {
            scores[methodId] = (scores[methodId] || 0) + 1;
          }
        });
      });

      return scores;
    },
    maxScore() {
      const scores = Object.values(this.methodScores);
      return scores.length > 0 ? Math.max(...scores) : 0;
    },
    winningMethods() {
      return Object.keys(this.methodScores).filter(
        (methodId) => this.methodScores[methodId] === this.maxScore,
      );
    },
  },
  methods: {
    ...mapActions(["setQuizResults"]),
    selectOption(index) {
      this.selectedOptionIndex = index;
    },
    handleBack() {
      if (this.stepIndex === 0) {
        this.goHome();
        return;
      }
      this.stepIndex -= 1;
      // Drop the answer for the step we're leaving — the user re-answers,
      // matching the step-by-step quiz's simple back behavior.
      this.answers = this.answers.slice(0, this.stepIndex);
      this.selectedOptionIndex = null;
    },
    handleNext() {
      if (this.selectedOptionIndex === null) return;
      const chosen = this.currentOptions[this.selectedOptionIndex];
      const answer = { id: this.currentQuestion.id, methods: chosen.methods };
      this.answers = [...this.answers.slice(0, this.stepIndex), answer];

      if (!this.isLastQuestion) {
        this.stepIndex += 1;
        this.selectedOptionIndex = null;
      } else {
        this.submitQuiz();
      }
    },
    goHome() {
      this.$router.push({
        name: "HomePage",
      });
    },
    submitQuiz() {
      console.log("Method Scores:", this.methodScores);
      console.log("Winning Methods:", this.winningMethods);

      // Save results to store
      this.setQuizResults({
        winningMethods: this.winningMethods,
        selectedChoices: this.answers,
        methodScores: this.methodScores,
      });

      // Track questions whose answer actually contributed methods
      const choiceTexts = this.sortedQuestions
        .filter((question, index) => {
          const answer = this.answers[index];
          return answer && answer.methods.length > 0;
        })
        .map((question) => question.text)
        .join(", ");
      this.$gtag.event("Quiz Completed", {
        event_category: "quiz_completed",
        event_label: "quiz_choices",
        value: choiceTexts,
      });

      // Navigate to results page
      this.$router.push({
        name: "QuizResultsPage",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// QuizPageOne specific styles

.page-container {
  height: 100vh;
  position: relative;
  overflow-y: scroll; // Always reserve the scrollbar gutter so content width stays stable
  overflow-x: hidden;
  background: transparent;
  scrollbar-width: thin; // Firefox — force a persistent, non-overlay scrollbar
  scrollbar-color: rgba(255, 255, 255, 0.7) rgba(255, 255, 255, 0.15);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.15);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
  }
}

.full-page-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
}

.page-header {
  flex-shrink: 0;
  padding: 40px 24px 20px 24px;
  text-align: center;
  position: relative;
  z-index: 1;

  @media (max-height: 480px) and (orientation: landscape) {
    padding: 16px 24px 12px 24px;
  }
}

.page-title {
  color: white;
  font-size: 32px;
  font-weight: 900;
  font-family: "Montserrat", sans-serif;
  text-align: center;
  margin: 0 0 12px 0;
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.page-subtitle {
  color: white;
  font-size: 16px;
  font-weight: 500;
  font-family: "Montserrat", sans-serif;
  margin: 0;
  line-height: 1.3;
}

.quiz-bottom-inner {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.quiz-progress-bar {
  width: 100%;
  max-width: 480px;
  margin: 56px auto 0;
  padding: 12px 16px 14px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  box-sizing: border-box;
}

.quiz-progress-row {
  display: flex;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: "Montserrat", sans-serif;
  margin-bottom: 8px;
}

.quiz-progress-track {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 99px;
  height: 8px;
  overflow: hidden;
}

.quiz-progress-fill {
  height: 100%;
  border-radius: 99px;
  background: #eba614;
  border: 1px solid #ffffff;
  box-sizing: border-box;
  transition: width 0.4s ease;
}

.content-section {
  background: transparent;
  border-radius: 0;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: visible; // Let the whole page scroll instead of just this section
}

.content-inner {
  flex: 1 0 auto;
  overflow: visible;
  min-height: auto;
  margin-bottom: 0;
}

.question-area {
  padding: 32px 24px 130px 24px;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.question-emoji {
  font-size: 3rem;
  margin-bottom: 12px;
}

.question-text {
  color: #ffffff;
  font-size: 1.35rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  margin-bottom: 24px;
  line-height: 1.35;
}

.quiz-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 480px;
  text-align: left;
}

.choice-item {
  display: flex;
  flex-direction: column;
}

.choice-btn {
  background: #ffffff !important;
  color: #333333 !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  border-radius: 16px !important;
  min-height: 60px !important;
  height: auto !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  padding: 14px 20px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  width: 100% !important;
  white-space: normal !important;
  transition: all 0.2s !important;

  :deep(.v-btn__content) {
    width: 100% !important;
    height: auto !important;
    align-items: center !important;
    padding: 4px 0 !important;
  }

  &.active {
    background: #3d80e8 !important;
    color: #ffffff !important;
    border-color: #3d80e8 !important;
    box-shadow: 0 4px 20px rgba(61, 128, 232, 0.35) !important;
    transform: scale(1.02);
  }
}

.choice-marker {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #3d80e8;
  background: transparent;
  flex-shrink: 0;
  margin-right: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &.active {
    border-color: #ffffff;
    background: #ffffff;
  }
}

.choice-marker-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3d80e8;
  opacity: 0;
  transform: scale(0.4);
  transition: all 0.2s;
}

.choice-marker.active .choice-marker-dot {
  opacity: 1;
  transform: scale(1);
}

.choice-text {
  text-align: left;
  flex: 1;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  min-height: 24px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  flex: 1;
}

.bottom-bar {
  border: none !important;
}

.back-button {
  background: #eb63eb !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  font-size: 16px !important;
  text-transform: none !important;
  padding: 0 32px !important;
  height: 50px !important;
  min-width: 120px !important;
  box-shadow: 0 4px 12px rgba(235, 99, 235, 0.3) !important;
}

.submit-button {
  background: #eb63eb !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  font-size: 16px !important;
  text-transform: none !important;
  padding: 0 32px !important;
  height: 50px !important;
  min-width: 120px !important;
  box-shadow: 0 4px 12px rgba(235, 99, 235, 0.3) !important;

  &:disabled {
    background: #ccc !important;
    color: #666 !important;
    box-shadow: none !important;
  }
}

// Override global styles for this page — transparent bottom bar, matching HomePage
:deep(.v-app-bar) {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
